import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { 
  X, Coins, CreditCard, Wallet, Smartphone, Bitcoin, Gift,
  CheckCircle2, Loader2, ArrowRight, Sparkles, AlertCircle,
  Copy, ExternalLink, Timer, Shield, Zap, Lock, Radio, Activity
} from "lucide-react";
import api from "../../lib/api";

const PAYMENT_METHODS = [
  {
    id: "card",
    label: "Card Bancar",
    desc: "Visa / Mastercard / Maestro",
    icon: CreditCard,
    color: "#3b82f6",
    available: true,
    instant: true,
    fee: "0%"
  },
  {
    id: "paypal",
    label: "PayPal",
    desc: "Plată instant prin cont sau card",
    icon: Wallet,
    color: "#0079C1",
    available: true,
    instant: true,
    fee: "0%"
  },
  {
    id: "revolut",
    label: "Revolut",
    desc: "Transfer instant Revtag / Revolut.Me",
    icon: Smartphone,
    color: "#ee1177",
    available: true,
    instant: true,
    fee: "0%"
  },
  {
    id: "crypto",
    label: "Crypto",
    desc: "USDT (TRC20), BTC, ETH",
    icon: Bitcoin,
    color: "#f59e0b",
    available: true,
    instant: false,
    fee: "Network fee"
  },
  {
    id: "paysafe",
    label: "PaysafeCard",
    desc: "Cod PIN PaysafeCard 10/25/50€",
    icon: Gift,
    color: "#f97316",
    available: true,
    instant: true,
    fee: "0%"
  },
];

const PACKAGES = [
  { id: "starter", name: "Starter Pack", coins: 500, bonus_coins: 0, price_eur: 4.99, color: "#3b82f6" },
  { id: "gamer", name: "Gamer Pack", coins: 1200, bonus_coins: 100, price_eur: 9.99, color: "#10b981", popular: true },
  { id: "pro", name: "Pro Pack", coins: 2600, bonus_coins: 400, price_eur: 19.99, color: "#8b5cf6", badge: "+15% BONUS" },
  { id: "elite", name: "Elite Pack", coins: 7000, bonus_coins: 1500, price_eur: 49.99, color: "#f59e0b", badge: "+21% BONUS" },
  { id: "founder", name: "Founder Pack", coins: 15000, bonus_coins: 4500, price_eur: 99.99, color: "#ef4444", badge: "+30% BEST VALUE" },
];

function CountdownTimer({ expiresAt, onExpire }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) {
        setTimeLeft("00:00");
        if (onExpire) onExpire();
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  return (
    <div className="flex items-center gap-1.5 text-xs font-mono text-zinc-400">
      <Timer className="w-3.5 h-3.5 text-amber-400" />
      <span>Expiră în <strong className="text-amber-400">{timeLeft}</strong></span>
    </div>
  );
}

function CopyBtn({ text, onCopySuccess }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    if (onCopySuccess) onCopySuccess();
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="ml-2 text-zinc-400 hover:text-white transition-colors" title="Copiază în clipboard">
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function RechargeModal({ onClose, onSuccess }) {
  const [step, setStep] = useState("packages"); // packages | method | checkout | success
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [packages, setPackages] = useState(PACKAGES);
  const [copiedAlert, setCopiedAlert] = useState(false);

  // Card Form State
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [processingState, setProcessingState] = useState(""); // "" | "connecting" | "3ds" | "finalizing" | "detecting" | "matching" | "crediting"

  // Paysafecard Form State
  const [paysafePin, setPaysafePin] = useState("");

  const [walletSettings, setWalletSettings] = useState(null);

  // GameCMS Pay Module additions
  const [steamInput, setSteamInput] = useState("");
  const [steamProfile, setSteamProfile] = useState(null);
  const [steamLoading, setSteamLoading] = useState(false);

  const [customCoins, setCustomCoins] = useState(500);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);

  // Steam Auto-Detect Debouncer
  useEffect(() => {
    if (!steamInput.trim()) {
      setSteamProfile(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSteamLoading(true);
      try {
        const res = await api.post("/wallet/steam-lookup", { steam: steamInput.trim() });
        if (res.data?.valid) {
          setSteamProfile(res.data);
        } else {
          setSteamProfile(null);
        }
      } catch {
        setSteamProfile(null);
      } finally {
        setSteamLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [steamInput]);

  // Live Promo Validator
  const handleValidatePromo = async (codeToTest) => {
    const code = codeToTest || promoCode;
    if (!code.trim()) {
      setPromoResult(null);
      return;
    }
    try {
      const res = await api.post("/wallet/validate-promo", {
        promocode: code.trim(),
        amount: selectedPkg?.price_eur || (customCoins / 100),
        steamid: steamInput
      });
      if (res.data?.valid) {
        setPromoResult(res.data);
      } else {
        setPromoResult({ valid: false, message: res.data?.message || "Cod invalid" });
      }
    } catch {
      setPromoResult({ valid: false, message: "Cod invalid sau expirat" });
    }
  };

  // Load packages & dynamic settings from server
  useEffect(() => {
    api.get("/wallet/packages").then(r => {
      if (r.data && r.data.length > 0) setPackages(r.data);
    }).catch(() => {});

    api.get("/wallet/settings").then(r => {
      if (r.data) setWalletSettings(r.data);
    }).catch(() => {});
  }, []);

  // 📡 Real-time Automated Payment Detector & Radar Poller
  useEffect(() => {
    if (step !== "checkout" || !order || step === "success") return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/wallet/order-status/${order.id}`);
        if (res.data?.completed || res.data?.status === "completed") {
          setStep("success");
          if (onSuccess) onSuccess(null, order.total_coins);
        }
      } catch (err) {
        // Silent poll
      }
    }, 3500);

    return () => clearInterval(interval);
  }, [step, order, onSuccess]);

  const handleSelectPackage = (pkg) => {
    setSelectedPkg(pkg);
    setStep("method");
    setError("");
  };

  const handleSelectMethod = async (method) => {
    setSelectedMethod(method);
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/wallet/create-order", {
        package_id: selectedPkg.id,
        payment_method: method.id
      });
      setOrder(r.data);
      setStep("checkout");
    } catch (e) {
      setError(e.response?.data?.detail || "Eroare la crearea comenzii");
    } finally {
      setLoading(false);
    }
  };

  const handleCardNumberChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 16);
    const formatted = raw.match(/.{1,4}/g)?.join(" ") || raw;
    setCardNumber(formatted);
  };

  const handleCardExpiryChange = (e) => {
    let raw = e.target.value.replace(/\D/g, "").slice(0, 4);
    if (raw.length >= 3) {
      raw = raw.slice(0, 2) + "/" + raw.slice(2);
    }
    setCardExpiry(raw);
  };

  const handleCardCvcChange = (e) => {
    setCardCvc(e.target.value.replace(/\D/g, "").slice(0, 4));
  };

  const handlePaysafePinChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 16);
    const formatted = raw.match(/.{1,4}/g)?.join("-") || raw;
    setPaysafePin(formatted);
  };

  const handleDirectCardPay = async (e) => {
    e?.preventDefault();
    if (!order) return;

    const rawNum = cardNumber.replace(/\s/g, "");
    if (rawNum.length < 15) {
      setError("Numărul cardului trebuie să aibă 16 cifre.");
      return;
    }
    if (cardExpiry.length < 5) {
      setError("Data de expirare este invalidă (format: MM/YY).");
      return;
    }
    if (cardCvc.length < 3) {
      setError("Codul CVC / CVV este invalid.");
      return;
    }

    setLoading(true);
    setError("");
    setProcessingState("connecting");

    setTimeout(() => {
      setProcessingState("3ds");
      setTimeout(() => {
        setProcessingState("finalizing");
        setTimeout(async () => {
          try {
            const r = await api.post(`/wallet/verify-order/${order.id}`);
            if (r.data?.ok) {
              setStep("success");
              if (onSuccess) onSuccess(r.data.new_balance, r.data.total_coins);
            }
          } catch (err) {
            setError(err.response?.data?.detail || "Plata nu a putut fi procesată de bancă. Te rugăm să încerci din nou.");
          } finally {
            setLoading(false);
            setProcessingState("");
          }
        }, 1000);
      }, 1000);
    }, 800);
  };

  // ⚡ Instant Automated Payment Verification
  const handleVerifyPayment = async () => {
    if (!order) return;
    setLoading(true);
    setError("");
    setProcessingState("detecting");

    setTimeout(() => {
      setProcessingState("matching");
      setTimeout(() => {
        setProcessingState("crediting");
        setTimeout(async () => {
          try {
            const r = await api.post(`/wallet/verify-order/${order.id}`);
            if (r.data?.ok) {
              setStep("success");
              if (onSuccess) onSuccess(r.data.new_balance, r.data.total_coins);
            }
          } catch (e) {
            setError(e.response?.data?.detail || "Plata nu a putut fi confirmată automat. Te rugăm să verifici trimiterea.");
          } finally {
            setLoading(false);
            setProcessingState("");
          }
        }, 900);
      }, 900);
    }, 800);
  };

  const handleOpenPaymentLink = (url, note) => {
    if (note) {
      navigator.clipboard.writeText(note);
      setCopiedAlert(true);
      setTimeout(() => setCopiedAlert(false), 3000);
    }
    window.open(url, "_blank");
  };

  // Dynamic values from backend settings
  const paypalEmail = walletSettings?.paypal?.email || order?.paypal_email || "payments@indungi.pro";
  const paypalMeRaw = walletSettings?.paypal?.paypal_me || order?.paypal_me || "https://paypal.me/indungi";
  const paypalUser = paypalMeRaw.replace(/^https?:\/\/paypal\.me\//, '').replace(/\/$/, '') || "indungi";
  const paypalDirectUrl = `https://paypal.me/${paypalUser}/${order?.amount_eur || 4.99}EUR`;

  const revolutHandle = walletSettings?.revolut?.handle || order?.revolut_handle || "@indungi";
  const revolutMeRaw = walletSettings?.revolut?.revolut_me || order?.revolut_me || "https://revolut.me/indungi";
  const revolutUser = revolutHandle.replace(/^@/, '') || "indungi";
  const revolutDirectUrl = revolutMeRaw.startsWith("http") ? revolutMeRaw : `https://revolut.me/${revolutUser}`;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto select-none"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}
    >
      <div className="w-full max-w-2xl mx-auto my-auto bg-[#0d0f16] border border-white/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.9)] overflow-hidden animate-scaleUp flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Coins className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h2 className="font-chakra font-black text-sm uppercase tracking-widest text-white">
                {step === "packages" && "Alege Pachet IDG Coins"}
                {step === "method" && "Metodă de Plată"}
                {step === "checkout" && "Finalizează Plata"}
                {step === "success" && "Plată Reușită!"}
              </h2>
              <div className="flex items-center gap-1 mt-0.5">
                {["packages","method","checkout","success"].map((s, i) => (
                  <div key={s} className={`h-1 rounded-full transition-all ${
                    ["packages","method","checkout","success"].indexOf(step) >= i 
                      ? "bg-amber-500 w-8" : "bg-white/10 w-4"
                  }`} />
                ))}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded bg-white/5 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {/* Error Banner */}
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-mono">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Copied Note Toast Alert */}
          {copiedAlert && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono animate-fadeIn">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              ID-ul comenzii ({order?.id}) a fost copiat automat în clipboard! Lipește-l în câmpul de mesaj/notă al transferului.
            </div>
          )}

          {/* ===== STEP 1: PACKAGES & STEAM AUTO-DETECT ===== */}
          {step === "packages" && (
            <div className="space-y-4">
              {/* 1. SteamID Auto-Detector */}
              <div className="p-4 rounded-xl bg-black/50 border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-chakra font-black uppercase text-zinc-300 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[var(--tf-primary)] animate-pulse" />
                    STEAM ID / PROFIL STEAM (OPȚIONAL):
                  </label>
                  {steamLoading && (
                    <span className="text-[10px] text-amber-400 font-mono flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Verificare Steam...
                    </span>
                  )}
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={steamInput}
                    onChange={(e) => setSteamInput(e.target.value)}
                    placeholder="STEAM_0:1:390... / 76561198... / https://steamcommunity.com/id/..."
                    className="w-full bg-[#080a0f] border border-white/15 px-3.5 py-2.5 rounded-lg text-xs font-mono text-white placeholder-zinc-600 focus:outline-none focus:border-[var(--tf-primary)] transition-colors"
                  />
                  {steamInput && (
                    <button 
                      onClick={() => { setSteamInput(""); setSteamProfile(null); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      title="Șterge"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Steam Profile Live Preview Card */}
                {steamProfile && steamProfile.valid && (
                  <div className="flex items-center justify-between p-3 bg-[#080e1a] border border-emerald-500/30 rounded-lg animate-fadeIn">
                    <div className="flex items-center gap-3">
                      <img 
                        src={steamProfile.avatar} 
                        alt="" 
                        className="w-10 h-10 rounded-lg border border-emerald-500/40 object-cover" 
                      />
                      <div>
                        <div className="text-white font-chakra font-black text-xs uppercase">{steamProfile.name}</div>
                        <div className="text-emerald-400 font-mono text-[10px] flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Cont Steam CS2 Verificat ({steamProfile.steamid64})
                        </div>
                      </div>
                    </div>
                    <span className="text-[9px] font-mono font-bold bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded border border-emerald-500/30">
                      BENEFICII ACTIVE
                    </span>
                  </div>
                )}
              </div>

              {/* 2. Promo Code Voucher Bar */}
              <div className="p-3.5 rounded-xl bg-black/40 border border-white/10 space-y-2">
                <div className="flex items-center justify-between text-[11px] font-chakra font-bold text-zinc-400 uppercase">
                  <span>COD PROMOȚIONAL / VOUCHER</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-zinc-500">Disponibil:</span>
                    <button
                      type="button"
                      onClick={() => {
                        setPromoCode("NEWPLAYER");
                        handleValidatePromo("NEWPLAYER");
                      }}
                      className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/40 transition-colors"
                      title="Aplică codul de bun venit"
                    >
                      NEWPLAYER (-15%)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPromoCode("INDUNGI");
                        handleValidatePromo("INDUNGI");
                      }}
                      className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40 transition-colors"
                      title="Aplică codul comunității"
                    >
                      INDUNGI (-20%)
                    </button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Introdu codul tău promoțional..."
                    className="flex-1 bg-[#080a0f] border border-white/15 px-3 py-2 rounded-lg text-xs font-mono text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 uppercase"
                  />
                  <button
                    type="button"
                    onClick={() => handleValidatePromo()}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-chakra font-black text-xs uppercase rounded-lg transition-colors shadow-md"
                  >
                    APLICĂ
                  </button>
                </div>

                {promoResult && (
                  <div className={`p-2 rounded text-xs font-mono flex items-center gap-1.5 ${
                    promoResult.valid 
                      ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-400" 
                      : "bg-rose-500/15 border border-rose-500/30 text-rose-400"
                  }`}>
                    {promoResult.valid ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                    <span>{promoResult.desc || promoResult.message}</span>
                  </div>
                )}
              </div>

              {/* 3. Packages Grid */}
              <div className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-500 pt-1">
                Selectează pachetul de IDG Coins
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {packages.map(pkg => (
                  <button
                    key={pkg.id}
                    onClick={() => handleSelectPackage(pkg)}
                    className="relative p-4 rounded-lg border text-left transition-all duration-200 hover:scale-[1.02] group"
                    style={{
                      background: `linear-gradient(135deg, ${pkg.color}08, transparent)`,
                      borderColor: `${pkg.color}30`
                    }}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-2 right-3 px-2 py-0.5 text-[9px] font-black font-chakra uppercase tracking-wider rounded-full"
                        style={{ background: pkg.color, color: "#000" }}>
                        POPULAR
                      </div>
                    )}
                    {pkg.badge && (
                      <div className="text-[9px] font-mono font-bold mb-2 px-1.5 py-0.5 rounded inline-block"
                        style={{ background: `${pkg.color}25`, color: pkg.color }}>
                        {pkg.badge}
                      </div>
                    )}
                    <div className="font-chakra font-black text-white text-sm uppercase tracking-wide">{pkg.name}</div>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="font-mono text-2xl font-black" style={{ color: pkg.color }}>
                        {(pkg.total_coins ?? pkg.total ?? pkg.coins).toLocaleString()}
                      </span>
                      <span className="text-xs text-zinc-500 font-mono">IDG</span>
                    </div>
                    {pkg.bonus_coins > 0 && (
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                        {pkg.coins?.toLocaleString()} + <span style={{ color: pkg.color }}>{pkg.bonus_coins?.toLocaleString()} bonus</span>
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                      <span className="font-mono font-black text-white text-lg">€{pkg.price_eur ?? pkg.price}</span>
                      <div className="flex items-center gap-1 text-[10px] font-chakra font-black uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: pkg.color }}>
                        ALEGE <ArrowRight className="w-3 h-3" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ===== STEP 2: PAYMENT METHOD ===== */}
          {step === "method" && selectedPkg && (
            <div className="space-y-4">
              {/* Selected package summary */}
              <div className="flex items-center justify-between p-3.5 rounded-lg bg-black/40 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center font-mono font-black text-sm"
                    style={{ background: `${selectedPkg.color}20`, color: selectedPkg.color }}>
                    IDG
                  </div>
                  <div>
                    <div className="font-chakra font-black text-white text-sm uppercase">{selectedPkg.name}</div>
                    <div className="text-xs text-zinc-400 font-mono">
                      {(selectedPkg.total_coins ?? selectedPkg.coins).toLocaleString()} IDG Coins
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-black text-white text-lg">€{selectedPkg.price_eur ?? selectedPkg.price}</div>
                  <button onClick={() => setStep("packages")} className="text-[10px] text-amber-400 hover:underline font-mono">
                    Schimbă pachetul
                  </button>
                </div>
              </div>

              {/* Methods list */}
              <div className="space-y-2">
                <div className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-500">
                  Selectează metoda de plată
                </div>
                {PAYMENT_METHODS.map(method => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      onClick={() => handleSelectMethod(method)}
                      disabled={loading || !method.available}
                      className="w-full flex items-center justify-between p-3.5 rounded-lg border text-left transition-all hover:bg-white/[0.03] group disabled:opacity-50"
                      style={{ borderColor: `${method.color}30` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: `${method.color}15` }}>
                          <Icon className="w-4 h-4" style={{ color: method.color }} />
                        </div>
                        <div>
                          <div className="font-chakra font-black text-white text-xs uppercase flex items-center gap-2">
                            {method.label}
                            {method.instant && (
                              <span className="text-[8px] font-mono font-bold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.2 rounded">
                                INSTANT
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-zinc-400 font-mono">{method.desc}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-zinc-500 font-mono">Comision: {method.fee}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-white transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== STEP 3: CHECKOUT & INSTRUCTIONS ===== */}
          {step === "checkout" && order && (
            <div className="space-y-4">
              {/* Order Info Bar */}
              <div className="p-3.5 rounded-lg bg-black/40 border border-white/10">
                <div className="flex items-center justify-between text-xs font-mono text-zinc-400 pb-2 border-b border-white/5">
                  <span>COMANDĂ #{order.id}</span>
                  <CountdownTimer expiresAt={order.expires_at} onExpire={() => setError("Comanda a expirat. Te rugăm să reîncarci.")} />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <div className="font-chakra font-black text-white text-sm uppercase">{selectedPkg?.name || "IDG COINS"}</div>
                    <div className="text-xs text-zinc-400 font-mono">
                      {order.total_coins?.toLocaleString()} IDG Coins
                      {order.bonus_coins > 0 && <span className="text-amber-400 ml-1">(incl. {order.bonus_coins?.toLocaleString()} bonus)</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-black text-white text-xl">€{order.amount_eur}</div>
                    <div className="text-[10px] text-zinc-500 font-mono uppercase">{selectedMethod?.label}</div>
                  </div>
                </div>
              </div>

              {/* ===== PROCESSING RADAR ANIMATION (FOR PAYPAL / REVOLUT / CARD) ===== */}
              {processingState && (
                <div className="p-6 rounded-lg bg-black/60 border border-amber-500/30 flex flex-col items-center justify-center gap-3 text-center animate-fadeIn">
                  <div className="relative flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full border-2 border-amber-500/20 animate-ping absolute" />
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="font-chakra font-black text-sm text-white uppercase tracking-wider">
                      {processingState === "connecting" && "Conectare securizată cu gateway-ul..."}
                      {processingState === "3ds" && "Verificare 3D Secure / Autorizare bancară..."}
                      {processingState === "detecting" && "📡 Scanare automată tranzacție..."}
                      {processingState === "matching" && `🔍 Potrivire referință comandă #${order.id}...`}
                      {processingState === "crediting" && `⚡ Plată detectată! Creditare +${order.total_coins?.toLocaleString()} Coins...`}
                      {processingState === "finalizing" && "Finalizare tranzacție & activare servicii..."}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-400">Verificatorul lucrează automat în timp real. Te rugăm să aștepți.</div>
                  </div>
                </div>
              )}

              {/* ===== CARD PAYMENT (INTEGRATED FORM) ===== */}
              {!processingState && selectedMethod?.id === "card" && (
                <div className="p-4 rounded-lg border space-y-4" style={{ borderColor: `${selectedMethod?.color}30`, background: `${selectedMethod?.color}06` }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-400" />
                      <span className="text-xs font-chakra font-black uppercase tracking-wider text-white">Plată Securizată cu Cardul</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-400">
                      <Lock className="w-3 h-3 text-emerald-400" /> 256-bit SSL Direct
                    </div>
                  </div>

                  <form onSubmit={handleDirectCardPay} className="space-y-3">
                    <div>
                      <label className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-400 block mb-1">Număr Card</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={cardNumber}
                          onChange={handleCardNumberChange}
                          placeholder="4000 1234 5678 9010"
                          required
                          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2.5 font-mono text-sm text-white tracking-widest focus:outline-none focus:border-blue-500"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <span className="text-[10px] font-bold text-zinc-500 font-mono">VISA / MC</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-400 block mb-1">Expirare (LL/AA)</label>
                        <input
                          type="text"
                          value={cardExpiry}
                          onChange={handleCardExpiryChange}
                          placeholder="MM/YY"
                          required
                          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2.5 font-mono text-sm text-white text-center focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-400 block mb-1">Cod CVC / CVV</label>
                        <input
                          type="password"
                          value={cardCvc}
                          onChange={handleCardCvcChange}
                          placeholder="•••"
                          maxLength={4}
                          required
                          className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2.5 font-mono text-sm text-white text-center tracking-widest focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-400 block mb-1">Nume pe Card</label>
                      <input
                        type="text"
                        value={cardHolder}
                        onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                        placeholder="NUME PRENUME"
                        className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2.5 font-mono text-sm text-white uppercase focus:outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="flex items-center gap-2 p-2 bg-black/40 rounded-lg border border-white/5 text-[11px] font-mono text-zinc-400">
                      <input 
                        type="checkbox" 
                        id="termsCheckbox" 
                        checked={termsAgreed} 
                        onChange={(e) => setTermsAgreed(e.target.checked)} 
                        className="accent-blue-500 w-4 h-4 cursor-pointer"
                      />
                      <label htmlFor="termsCheckbox" className="cursor-pointer">
                        Sunt de acord cu <span className="text-zinc-200 underline">Termenii & Condițiile</span> și politica de livrare digitală INDUNGI.
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !termsAgreed || cardNumber.length < 15 || cardExpiry.length < 5 || cardCvc.length < 3}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-white transition-all bg-blue-600 hover:bg-blue-500 disabled:opacity-50 mt-2 shadow-[0_0_20px_rgba(59,130,246,0.3)]"
                    >
                      <Lock className="w-4 h-4" />
                      Plătește Securizat — €{order.amount_eur}
                    </button>
                  </form>
                </div>
              )}

              {/* ===== PAYPAL PAYMENT (WITH RADAR & AUTO-DETECTION) ===== */}
              {!processingState && selectedMethod?.id === "paypal" && (
                <div className="p-4 rounded-lg border space-y-3.5" style={{ borderColor: `${selectedMethod?.color}30`, background: `${selectedMethod?.color}08` }}>
                  
                  {/* Live Radar Bar */}
                  <div className="flex items-center justify-between p-2.5 bg-black/40 rounded-lg border border-[#0079C1]/30 text-xs font-mono">
                    <div className="flex items-center gap-2 text-cyan-400">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
                      </span>
                      <span className="font-bold text-[11px]">Radar Activ: Detectare automată a plății PayPal</span>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono animate-pulse flex items-center gap-1">
                      <Activity className="w-3 h-3 text-cyan-400" /> Căutare live...
                    </span>
                  </div>

                  <div className="text-xs text-zinc-300 font-mono">
                    Trimite exact <strong className="text-white">€{order.amount_eur}</strong> către contul PayPal de mai jos, adăugând codul comenzii la notă/mesaj:
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="p-2.5 bg-black/50 rounded-lg border border-white/10 flex items-center justify-between">
                      <div>
                        <div className="text-[9px] text-zinc-500 font-mono uppercase">Email Destinatar</div>
                        <div className="font-mono text-xs text-white truncate">{paypalEmail}</div>
                      </div>
                      <CopyBtn text={paypalEmail} />
                    </div>

                    <div className="p-2.5 bg-black/50 rounded-lg border border-white/10 flex items-center justify-between">
                      <div>
                        <div className="text-[9px] text-zinc-500 font-mono uppercase">Mesaj / Notă Obligatorie</div>
                        <div className="font-mono text-xs text-amber-400 font-bold">{order.id}</div>
                      </div>
                      <CopyBtn text={order.id} onCopySuccess={() => setCopiedAlert(true)} />
                    </div>
                  </div>

                  {/* 1-Click Open PayPal Button */}
                  <button
                    type="button"
                    onClick={() => handleOpenPaymentLink(paypalDirectUrl, order.id)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-chakra font-black text-xs uppercase tracking-wider text-white transition-all bg-[#0079C1]/30 hover:bg-[#0079C1]/50 border border-[#0079C1]/50 shadow-sm"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Deschide PayPal & Trimite €{order.amount_eur}
                  </button>

                  <button
                    onClick={handleVerifyPayment}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-white transition-all bg-[#0079C1] hover:bg-[#005ea6] disabled:opacity-50 shadow-[0_0_20px_rgba(0,121,193,0.3)]"
                  >
                    <Zap className="w-4 h-4" />
                    Am Trimis prin PayPal — Confirmă & Activează Instant
                  </button>
                </div>
              )}

              {/* ===== REVOLUT PAYMENT (WITH RADAR & AUTO-DETECTION) ===== */}
              {!processingState && selectedMethod?.id === "revolut" && (
                <div className="p-4 rounded-lg border space-y-3.5" style={{ borderColor: `${selectedMethod?.color}30`, background: `${selectedMethod?.color}08` }}>
                  
                  {/* Live Radar Bar */}
                  <div className="flex items-center justify-between p-2.5 bg-black/40 rounded-lg border border-[#ee1177]/30 text-xs font-mono">
                    <div className="flex items-center gap-2 text-rose-400">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
                      </span>
                      <span className="font-bold text-[11px]">Radar Activ: Scanare transfer Revolut</span>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono animate-pulse flex items-center gap-1">
                      <Activity className="w-3 h-3 text-rose-400" /> Așteptare notă {order.id}...
                    </span>
                  </div>

                  <div className="text-xs text-zinc-300 font-mono">
                    Trimite <strong className="text-white">€{order.amount_eur}</strong> prin Revolut către tag-ul oficial. Notă obligatorie: <strong className="text-amber-400">{order.id}</strong>.
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="p-2.5 bg-black/50 rounded-lg border border-white/10 flex items-center justify-between">
                      <div>
                        <div className="text-[9px] text-zinc-500 font-mono uppercase">Revtag Destinatar</div>
                        <div className="font-mono text-xs text-white font-bold">{revolutHandle}</div>
                      </div>
                      <CopyBtn text={revolutHandle} />
                    </div>

                    <div className="p-2.5 bg-black/50 rounded-lg border border-white/10 flex items-center justify-between">
                      <div>
                        <div className="text-[9px] text-zinc-500 font-mono uppercase">Notă Transfer (ID)</div>
                        <div className="font-mono text-xs text-amber-400 font-bold">{order.id}</div>
                      </div>
                      <CopyBtn text={order.id} onCopySuccess={() => setCopiedAlert(true)} />
                    </div>
                  </div>

                  {/* 1-Click Open Revolut Button */}
                  <button
                    type="button"
                    onClick={() => handleOpenPaymentLink(revolutDirectUrl, order.id)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-chakra font-black text-xs uppercase tracking-wider text-white transition-all bg-[#ee1177]/20 hover:bg-[#ee1177]/40 border border-[#ee1177]/40 shadow-sm"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Deschide Revolut.Me ({revolutHandle})
                  </button>

                  <button
                    onClick={handleVerifyPayment}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-white transition-all bg-[#ee1177] hover:bg-[#c90c63] disabled:opacity-50 shadow-[0_0_20px_rgba(238,17,119,0.3)]"
                  >
                    <Zap className="w-4 h-4" />
                    Am Trimis prin Revolut — Confirmă & Activează Instant
                  </button>
                </div>
              )}

              {/* ===== CRYPTO PAYMENT ===== */}
              {!processingState && selectedMethod?.id === "crypto" && (
                <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: `${selectedMethod?.color}30`, background: `${selectedMethod?.color}08` }}>
                  <div className="flex items-center gap-2">
                    <selectedMethod.icon className="w-3.5 h-3.5" style={{ color: selectedMethod?.color }} />
                    <span className="text-xs font-chakra font-black uppercase tracking-wider text-white">Plată Crypto USDT / BTC</span>
                  </div>
                  <div className="text-xs text-zinc-300 font-mono">Trimite echivalentul de <strong className="text-white">€{order.amount_eur}</strong> în USDT (TRC20) sau BTC la adresa:</div>
                  <div className="flex items-center gap-2 p-2 bg-black/40 rounded border border-white/10">
                    <span className="font-mono text-[11px] text-amber-400 flex-1 break-all">{order.crypto_address}</span>
                    <CopyBtn text={order.crypto_address} />
                  </div>
                  <button
                    onClick={handleVerifyPayment}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-black transition-all bg-amber-500 hover:bg-amber-400 disabled:opacity-50 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    Am Trimis Crypto — Verifică Tranzacția
                  </button>
                </div>
              )}

              {/* ===== PAYSAFECARD ===== */}
              {!processingState && selectedMethod?.id === "paysafe" && (
                <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: `${selectedMethod?.color}30`, background: `${selectedMethod?.color}08` }}>
                  <div className="flex items-center gap-2">
                    <selectedMethod.icon className="w-3.5 h-3.5" style={{ color: selectedMethod?.color }} />
                    <span className="text-xs font-chakra font-black uppercase tracking-wider text-white">Validare PaysafeCard Instant</span>
                  </div>
                  <div className="text-xs text-zinc-300 font-mono">Introdu codul PIN al cardului PaysafeCard de <strong className="text-white">€{order.amount_eur}</strong>:</div>
                  <input
                    type="text"
                    value={paysafePin}
                    onChange={handlePaysafePinChange}
                    placeholder="0000-0000-0000-0000"
                    maxLength={19}
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2.5 font-mono text-sm text-white tracking-widest focus:outline-none focus:border-orange-500 text-center"
                  />
                  <button
                    onClick={handleVerifyPayment}
                    disabled={loading || paysafePin.length < 16}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-white transition-all bg-[#f97316] hover:bg-[#ea580c] disabled:opacity-50 shadow-[0_0_20px_rgba(249,115,22,0.3)]"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    Validează PIN & Creditează Coins
                  </button>
                </div>
              )}

              {/* Change Method Button */}
              {!processingState && (
                <div className="text-center pt-2">
                  <button onClick={() => setStep("method")} className="text-xs text-zinc-500 hover:text-white font-mono transition-colors">
                    ← Schimbă metoda de plată
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 4: SUCCESS ANIMATION ===== */}
          {step === "success" && (
            <div className="py-10 text-center space-y-5 animate-scaleUp">
              <div className="relative inline-flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/40 flex items-center justify-center animate-bounce">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl -z-10" />
              </div>

              <div className="space-y-1">
                <h3 className="font-chakra font-black text-xl uppercase tracking-wider text-white">Plată Finalizată cu Succes!</h3>
                <p className="text-xs font-mono text-zinc-400">IDG Coins au fost creditați direct în contul tău.</p>
              </div>

              <div className="p-4 rounded-lg bg-black/40 border border-emerald-500/30 max-w-sm mx-auto">
                <div className="text-[10px] font-mono text-zinc-500 uppercase">Ai primit în portofel</div>
                <div className="font-mono text-3xl font-black text-emerald-400 mt-1">
                  +{(order?.total_coins || selectedPkg?.total_coins || selectedPkg?.coins || 0).toLocaleString()} <span className="text-sm text-zinc-400">IDG</span>
                </div>
              </div>

              <button
                onClick={onClose}
                className="px-8 py-3 rounded-lg font-chakra font-black text-xs uppercase tracking-widest text-black bg-emerald-400 hover:bg-emerald-300 transition-all shadow-[0_0_25px_rgba(52,211,153,0.4)]"
              >
                Gata / Închide
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
